import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./AdminDashboard.scss";

const AdminDashboard = () => {
  const { hasRole, hasAnyAuthority, user } = useAuth();

  // Checks whether any of the given moduleAccessIDs exist in user.moduleAccess
  const hasModuleAccess = (moduleAccessIDs = []) =>
    Array.isArray(user?.moduleAccess) &&
    user.moduleAccess.some((m) => moduleAccessIDs.includes(m.moduleAccessID));

  const modules = [
    {
      name: "Company Details",
      path: "/company-details/info",
      description: "Company Configuration",
      icon: "🏢",
      explanation:
        "Configure company information, organizational structure, branch details, and corporate settings. Manage company-wide policies and configurations.",
      isAccessible: hasRole("ROLE_ADMIN") && hasModuleAccess(["ADMIN"]),
    },
    {
      name: "HRM",
      path: "/admin/leave-types",
      description: "Human Resource Management",
      icon: "👥",
      explanation:
        "Manage employee data, user management, payroll settings, attendance tracking, and organizational structure. Handle user roles and permissions for HR operations.",
      isAccessible:
        hasRole("ROLE_ADMIN") &&
        hasModuleAccess([
          "HRM",
          "LEAVE",
          "ATTENDANCE",
          "PAYROLL",
          "EMAIL_AUTOMATION",
        ]),
    },
    {
      name: "CRM",
      path: "/crm/inventory/master",
      description: "Customer Relationship Management",
      icon: "🤝",
      explanation:
        "Set up customer databases, sales pipeline configurations, communication templates, and lead management workflows. Customize client interaction tracking.",
      isAccessible:
        hasAnyAuthority([
          "ROLE_CRM-ADMIN",
          "ROLE_ERP-ADMIN",
          "ROLE_INVENTORY-MANAGER",
        ]) && hasModuleAccess(["CRM"]),
    },
    {
      name: "Notification",
      path: "/admin/notification",
      description: "Notification Management",
      icon: "🔔",
      explanation:
        "Browse notification modules and categories, view scheduled event templates by event type, and explore launched notification templates for tracking and reuse.",
      isAccessible: hasRole("ROLE_ADMIN"),
    },
    {
      name: "Automation",
      path: "/admin/automation",
      description: "Automation Management",
      icon: "⚙️",
      explanation:
        "Browse automation modules and categories, view scheduled cron job templates by job type, and explore launched automation templates for tracking and reuse.",
      isAccessible: hasRole("ROLE_ADMIN"),
    },
  ];

  const ModuleCardContent = ({ mod }) => (
    <>
      <div className="card-icon">{mod.icon}</div>
      <h3 className="card-title">{mod.name}</h3>
      <p className="card-description">{mod.description}</p>
      <p className="card-explanation">{mod.explanation}</p>
      {!mod.isAccessible && (
        <span className="no-access-label">
          You don't have access to this page
        </span>
      )}
    </>
  );

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Configure and manage system settings</p>
      </div>
      <div className="dashboard-grid">
        {modules.map((mod) =>
          mod.isAccessible ? (
            <Link key={mod.name} to={mod.path} className="module-card">
              <ModuleCardContent mod={mod} />
            </Link>
          ) : (
            <div key={mod.name} className="module-card disabled">
              <ModuleCardContent mod={mod} />
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
